import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Container from '~/components/Container';
import Button from '~/components/Button';
import ProvidedBy from '~/components/ProvidedBy';
import { useAuth } from '~/lib/auth';
import { useTitle } from '~/lib/useTitle';
import s from './Payment.module.scss';

const PLANS = ['monthly', 'yearly', 'lifetime'];

export default function Payment() {
  useTitle('Checkout — MetaHub');
  const profile = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const plan = new URLSearchParams(search).get('plan') || '';
  const validPlan = PLANS.includes(plan);

  const [sessionId, setSessionId] = useState('');
  const [error, setError] = useState('');

  // Not signed in → log in first, then come back here to the same plan.
  useEffect(() => {
    if (validPlan && !profile) {
      navigate(`/login?returnUrl=${encodeURIComponent(`/payment?plan=${plan}`)}`, { replace: true });
    }
  }, [validPlan, profile, plan, navigate]);

  // Create the checkout session once we have an authed user (uses the same Whop mem ids as stremio).
  useEffect(() => {
    if (!validPlan || !profile) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/billing/checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${profile.authKey}` },
          body: JSON.stringify({ subscription: plan }),
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data || !data.session_id) throw new Error((data && data.error) || 'Could not start checkout.');
        if (!cancelled) setSessionId(data.session_id);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [validPlan, profile, plan]);

  // The /payments iframe posts CHECKOUT_COMPLETED to its own origin when payment succeeds.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data && e.data.type === 'CHECKOUT_COMPLETED') navigate('/account?checkout=success');
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [navigate]);

  if (!validPlan) {
    return (
      <section className={s.wrap}>
        <Container>
          <div className={s.card}>
            <div className={s.kicker}>Checkout</div>
            <h1>Choose a plan</h1>
            <p className={s.sub}>Pick a MetaHub plan to continue to checkout.</p>
            <Button to="/pricing" variant="primary" className={s.cta}>View plans</Button>
          </div>
        </Container>
      </section>
    );
  }

  if (!profile) return null;

  return (
    <section className={s.wrap}>
      <Container>
        <div className={s.card}>
          <div className={s.kicker}>Checkout</div>
          <h1>Complete your purchase</h1>
          <p className={s.sub}>You're subscribing to the MetaHub {plan} plan.</p>

          {error ? (
            <div className={s.error}>{error}</div>
          ) : sessionId ? (
            <iframe title="Secure checkout" className={s.frame} src={`/payments?session=${encodeURIComponent(sessionId)}`} />
          ) : (
            <div className={s.muted}>Preparing secure checkout…</div>
          )}
          <ProvidedBy />
        </div>
      </Container>
    </section>
  );
}
